import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth.store';
import { authService } from '@/features/auth/services/auth.service';
import { Sidebar } from '@/components/layout/Sidebar';
import { Header } from '@/components/layout/Header';
import { MobileSidebar } from '@/components/layout/MobileSidebar';
import { MobileBottomNav } from '@/components/layout/MobileBottomNav';
import { cn } from '@/lib/utils';
import { AvisoModal } from '@/features/notifications/components/AvisoModal';
import { UnreadNotificationsModal } from '@/features/notifications/components/UnreadNotificationsModal';
import { NotificationIncomingToasts } from '@/features/notifications/components/NotificationIncomingToasts';
import { UnreadSummaryModalProvider } from '@/features/notifications/context/UnreadSummaryModalContext';
import { PomodoroModalProvider } from '@/contexts/PomodoroModalContext';

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: ({ location }) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href,
        },
      });
    }
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const user = useAuthStore((state) => state.user);
  const setAuth = useAuthStore((state) => state.setAuth);
  const token = useAuthStore((state) => state.token);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  // Sincronizar com mudanças no localStorage do sidebar
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('sidebar-collapsed');
      setIsSidebarCollapsed(saved === 'true');
    };

    // Verificar mudanças no localStorage
    const interval = setInterval(handleStorageChange, 100);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Atualizar permissões do usuário ao carregar a rota autenticada
  useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const userData = await authService.getMe();
      // Atualizar o store com as permissões atualizadas
      if (token && userData) {
        setAuth(
          {
            id: userData.id,
            email: userData.email,
            tenantId: userData.tenantId,
            permissions: userData.permissions,
          },
          token,
        );
      }
      return userData;
    },
    enabled: !!token, // Só executa se houver token
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
    refetchOnWindowFocus: true, // Atualizar quando a janela recebe foco
  });

  // Verificar se o perfil está completo para ajustar padding do conteúdo
  const { data: profileComplete } = useQuery({
    queryKey: ['profile-complete'],
    queryFn: async () => {
      const { profileService } = await import('@/features/profile/services/profile.service');
      return profileService.checkProfileComplete();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (!user) {
    return null;
  }

  return (
    <PomodoroModalProvider>
      <UnreadSummaryModalProvider>
      <div className="min-h-screen bg-page-background">
        <Header
          onMenuClick={() => setMobileMenuOpen(true)}
          isSidebarCollapsed={isSidebarCollapsed}
        />
        <Sidebar />
        <MobileSidebar open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      {/* Conteúdo principal com padding para acomodar sidebar e header flutuantes em desktop */}
      {/* Padding-top: 
          - Mobile: header fixo (64px) = 64px, ou header (64px) + alerta (~40px) = 104px se houver alerta
          - Desktop sem alerta: header (64px) + top (12px) + gap (12px) = 88px
          - Desktop com alerta: alerta (~40px) + header (64px) + top (12px) + gap (12px) = ~128px
      */}
      {/* Padding-left ajustado dinamicamente: sidebar expandido (256px + 12px margem) ou colapsado (64px + 12px margem) */}
      {/* Mobile: padding-top fixo para header fixo, sem padding-left do sidebar */}
      <main
        className={cn(
          'p-4 md:p-6 min-h-screen pb-20 md:pb-6 transition-all duration-300',
          // Padding-top no mobile: considerar possível alerta
          profileComplete && !profileComplete.isComplete
            ? 'pt-[calc(2.5rem+4rem)]' // Alerta (~40px) + header (64px)
            : 'pt-16', // Header (64px)
          // Padding-top no desktop: considerar header e possível alerta
          profileComplete && !profileComplete.isComplete
            ? 'md:pt-[calc(2.5rem+4rem+0.75rem+0.75rem)]' // Alerta (~40px) + header (64px) + top (12px) + gap (12px)
            : 'md:pt-[calc(4rem+0.75rem+0.75rem)]', // Header (64px) + top (12px) + gap (12px)
          // Padding-right no desktop
          'md:pr-6',
          // Alinhar left com o header
          isSidebarCollapsed
            ? 'md:pl-[calc(4rem+0.75rem+0.75rem)]' // Sidebar colapsado: 64px (4rem) + 12px margem + 12px gap
            : 'md:pl-[calc(16rem+0.75rem+0.75rem)]', // Sidebar expandido: 256px (16rem) + 12px margem + 12px gap
        )}
      >
        <div className="w-full max-w-[1920px] mx-auto">
          <Outlet />
        </div>
      </main>
        <MobileBottomNav />
        <NotificationIncomingToasts />
        <AvisoModal />
        <UnreadNotificationsModal />
      </div>
      </UnreadSummaryModalProvider>
    </PomodoroModalProvider>
  );
}
